import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  X,
  Buildings,
  EnvelopeSimple,
  User,
  Copy,
  CheckCircle,
  CircleNotch as Loader2,
  MagnifyingGlass as Search,
  ArrowRight,
} from '@phosphor-icons/react';
import { inviteSolicitor, type InviteSolicitorResult } from '@/lib/solicitorPortalApi';
import { searchContacts } from '@/lib/contactsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * InviteSolicitorModal — clerk-side flow.
 *
 * 1. Pick the firm contact (search the chambers' existing firm / solicitor contacts)
 * 2. Enter the solicitor's personal details (email, name, optional job title + SRA)
 * 3. Backend creates the User + SolicitorProfile + issues a magic-link token
 * 4. We show the returned link so the clerk can forward it manually while
 *    the email pipeline is still being wired up.
 */
export function InviteSolicitorModal({ open, onClose }: Props) {
  const [firmQuery, setFirmQuery] = useState('');
  const [firmResults, setFirmResults] = useState<Array<{ id: string; name: string; org: string }>>([]);
  const [firmContactId, setFirmContactId] = useState<string | null>(null);
  const [firmLabel, setFirmLabel] = useState<string>('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [sraNumber, setSraNumber] = useState('');
  const [copied, setCopied] = useState(false);

  const invite = useMutation({
    mutationFn: () =>
      inviteSolicitor({
        email,
        firstName,
        lastName,
        firmContactId: firmContactId!,
        jobTitle: jobTitle || undefined,
        sraNumber: sraNumber || undefined,
      }),
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not invite solicitor'));
    },
    onSuccess: () => {
      toast.success('Invitation created');
    },
  });

  // Search firms as the clerk types
  useEffect(() => {
    if (firmContactId) return; // once picked, stop searching
    const q = firmQuery.trim();
    if (q.length < 2) {
      setFirmResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Search BOTH contact types. The API accepts either for a portal link
        // (solicitor-portal.service.ts: type must be 'solicitor' or 'firm'), but this box
        // used to query 'solicitor' only - so a clerk who followed the instruction below
        // and added the firm as a Firm contact could never find it, and the invite could
        // not be completed for any genuinely new firm.
        const [asSolicitor, asFirm] = await Promise.all([
          searchContacts(q, 'solicitor'),
          searchContacts(q, 'firm'),
        ]);
        if (cancelled) return;
        const seen = new Set<string>();
        const merged = [...asSolicitor, ...asFirm].filter((r) => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
        setFirmResults(merged.map((r) => ({ id: r.id, name: r.name, org: r.org })));
      } catch {
        setFirmResults([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firmQuery, firmContactId]);

  if (!open) return null;

  const result: InviteSolicitorResult | undefined = invite.data;

  const reset = () => {
    setFirmQuery('');
    setFirmResults([]);
    setFirmContactId(null);
    setFirmLabel('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setJobTitle('');
    setSraNumber('');
    setCopied(false);
    invite.reset();
  };

  const canSubmit = !!firmContactId && !!email && !!firstName && !!lastName && !invite.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-brand-600 dark:text-brand-400">
              Solicitor Portal
            </div>
            <h2 className="text-base font-semibold text-foreground">Invite a solicitor</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 p-5">
          {!result && (
            <>
              {/* Firm picker */}
              <Field label="Firm" icon={<Buildings className="h-4 w-4" />}>
                {firmContactId ? (
                  <div className="flex items-center justify-between rounded-md border border-brand-200 bg-brand-50 dark:border-brand-500/30 dark:bg-brand-900/20 px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium text-foreground">{firmLabel}</div>
                    </div>
                    <button
                      onClick={() => {
                        setFirmContactId(null);
                        setFirmLabel('');
                      }}
                      className="text-xs text-brand-700 dark:text-brand-400 hover:underline"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={firmQuery}
                      onChange={(e) => setFirmQuery(e.target.value)}
                      placeholder="Search existing solicitor firms…"
                      className="h-10 w-full rounded-md border border-border bg-card pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
                    />
                    {firmResults.length > 0 && (
                      <div className="mt-1 overflow-hidden rounded-md border border-border bg-card shadow-sm">
                        {firmResults.map((r) => (
                          <button
                            key={r.id}
                            onClick={() => {
                              setFirmContactId(r.id);
                              setFirmLabel(r.name + (r.org && r.org !== '—' ? ` · ${r.org}` : ''));
                              setFirmResults([]);
                            }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                              <Buildings className="h-3.5 w-3.5" />
                            </div>
                            <div>
                              <div className="font-medium text-foreground">{r.name}</div>
                              {r.org && r.org !== '—' && (
                                <div className="text-xs text-muted-foreground">{r.org}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {firmQuery.trim().length >= 2 && firmResults.length === 0 && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        No matching firms. Add the firm on the Contacts page first.
                      </p>
                    )}
                  </div>
                )}
              </Field>

              {/* Person details */}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="First name" icon={<User className="h-4 w-4" />}>
                  <TextInput value={firstName} onChange={setFirstName} placeholder="e.g. Alice" />
                </Field>
                <Field label="Last name">
                  <TextInput value={lastName} onChange={setLastName} placeholder="e.g. Pritchard" />
                </Field>
              </div>

              <Field label="Email" icon={<EnvelopeSimple className="h-4 w-4" />}>
                <TextInput
                  value={email}
                  onChange={setEmail}
                  type="email"
                  placeholder="alice@firm.co.uk"
                />
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Job title (optional)">
                  <TextInput value={jobTitle} onChange={setJobTitle} placeholder="e.g. Partner" />
                </Field>
                <Field label="SRA number (optional)">
                  <TextInput value={sraNumber} onChange={setSraNumber} placeholder="6-digit SRA" />
                </Field>
              </div>

              <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                <b>What happens:</b> we create the account, then issue a one-time sign-in link that
                lasts 15 minutes. The solicitor clicks it and they're in. No password required.
              </div>
            </>
          )}

          {/* Success view */}
          {result && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10 p-4">
                <CheckCircle className="mt-0.5 h-5 w-5 text-emerald-600 dark:text-emerald-400" weight="fill" />
                <div className="text-sm">
                  <div className="font-semibold text-emerald-900 dark:text-emerald-200">
                    Invitation created for {result.user.firstName} {result.user.lastName}
                  </div>
                  <div className="mt-0.5 text-emerald-800 dark:text-emerald-300">{result.user.email}</div>
                </div>
              </div>

              {result.invite.devLink ? (
                <div>
                  <div className="mb-1.5 text-xs font-medium text-foreground">Sign-in link</div>
                  <div className="rounded-md border border-border bg-muted/40 p-3">
                    <div className="break-all text-xs text-foreground">
                      {result.invite.devLink}
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(result.invite.devLink!);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="h-3.5 w-3.5" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          Copy link
                        </>
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Dev mode: forward this link to the solicitor. In production, this is emailed automatically.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                  A sign-in link has been emailed to {result.user.email}.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          {result ? (
            <>
              <button
                onClick={reset}
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Invite another
              </button>
              <button
                onClick={onClose}
                className="inline-flex h-9 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                onClick={() => invite.mutate()}
                disabled={!canSubmit}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {invite.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                  </>
                ) : (
                  <>
                    Send invitation
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Form primitives ──────────────────────────────────────────

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {label}
      </div>
      {children}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
    />
  );
}
